import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AnnotationService } from 'src/app/services/annotation/annotation.service';
import { DataService } from 'src/app/services/data/data.service';
import { MapService } from 'src/app/services/map/map.service';
import { GenericTypeService } from 'src/app/services/generic-type/generic-type.service';
import { LocationComponentService } from 'src/app/services/locationComponent/location-component.service';
import { Subject } from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { FileService } from 'src/app/services/file/file.service';
import { dataURLtoFile } from 'src/app/helpers/file.helper';
import { NavigationService } from 'src/app/services/navigation/navigation.service';
import { PotreeCesiumViewerService } from 'src/app/components/potree-cesium-viewer/potree-cesium-viewer.service';
import { takeUntil } from 'rxjs/operators';
import { ChatService } from 'src/app/services/chat/chat.service';
import { TaskService } from 'src/app/services/task/task.service';
import { TranslateService } from '@ngx-translate/core';
import { ITaskForm } from 'src/app/components/task-form/task-form.component';
import { NgxImageCompressService } from 'ngx-image-compress';
import { LocationUIService } from 'src/app/services/location-ui/location-ui.service';
import { SplitViewService } from 'src/app/services/split-view/split-view.service';
import { LayerStoreService } from 'src/app/stores/layer-store/layer-store.service';
import { LocationStoreService } from 'src/app/stores/location-store/location-store.service';
import { GenericTypeStoreService } from 'src/app/stores/generic-types-store/generic-type-store.service';
import { OpenlayerService } from 'src/app/services/openlayer/openlayer.service';

declare var $;
declare let html2canvas: any;

@Component({
  selector: 'app-edit-annotation',
  templateUrl: './edit-annotation.component.html',
  styleUrls: ['./edit-annotation.component.sass'],
})
export class EditAnnotationComponent implements OnInit, OnDestroy {
  form: FormGroup;
  chatForm: FormGroup;

  taskForm: {
    /** Loaded value */
    _value: ITaskForm;
    /** Submitting value */
    value: ITaskForm;
    valid: boolean;
  };
  galleryForm: FormGroup;

  currentAnnotationId;
  currentAnnotation = null;

  chat;
  task;

  severityList = [];
  locationComponentList = [];
  currentLocation: any;
  actionBtn: boolean;
  isFormShow: Array<any> = [];
  mapLayerList = [];
  defectTypes = [];
  mapScreenshot: any;
  isOrtho: boolean;
  isDSM: boolean;
  isThermal: boolean;
  isThreeD: boolean;
  isThreeDMesh: boolean;

  hasInit: boolean = false;
  updatedMapScreenshot: boolean = false;

  /** Stores the route the user will go to after edit-annotation */
  exitRoute: string;

  unsubber: Subject<boolean> = new Subject<boolean>();
  currentUser: any;
  chats: Array<any> = [];

  isSquareMeter = true;

  constructor(
    private activatedRoute: ActivatedRoute,
    private annotationService: AnnotationService,
    private mapService: MapService,
    private dataService: DataService,
    private layerStore: LayerStoreService,
    private formBuilder: FormBuilder,
    private navigationService: NavigationService,
    private toastr: ToastrService,
    private genericTypeService: GenericTypeService,
    private locationComponentService: LocationComponentService,
    private spinner: NgxSpinnerService,
    private fileService: FileService,
    private potreeCesiumViewerService: PotreeCesiumViewerService,
    private chatService: ChatService,
    private taskService: TaskService,
    private _t: TranslateService,
    private imageCompress: NgxImageCompressService,
    private locUIService: LocationUIService,
    private router: Router,
    private splitViewService: SplitViewService,
    private locationStore: LocationStoreService,
    private genericTypeStore: GenericTypeStoreService,
    private openlayerService: OpenlayerService
  ) {
    // this.getSeverityList();
    this.getDefectTypeList();
  }

  ngOnInit(): void {
    this.mapService.isShiftKeyDown$.next(false);
    this.splitViewService.isEditingAnnotation = true;
    this.mapService.removeAllGrid();
    this.potreeCesiumViewerService.removeAllMeasurements();
    this.initForms();
    this.mapScreenshot = undefined; //to reset mapScreenshot
    this.currentUser = JSON.parse(localStorage.getItem('currentUser'));
    this.activatedRoute.params
      .pipe(takeUntil(this.unsubber.asObservable()))
      .subscribe(({ id }) => {
        this.annotationService
          .findOne(id)
          .pipe(takeUntil(this.unsubber.asObservable()))
          .subscribe((annotation) => {
            /** As per #141, all annotation will need to be turned off while editing */
            // this.mapService.toggleAllAnnotation(false, undefined, undefined);
            this.layerStore.toggleAllAnnotationDataLayer(false);

            this.mapService.activeAnnotation$
              .pipe(takeUntil(this.unsubber.asObservable()))
              .subscribe((area) => {
                // delete area.grids
                const _area = area;
                if (_area) {
                  if (area.drawing.length === 0 && area.grids.length === 0) {
                    return;
                  }
                  this.form.patchValue({
                    area: _area.drawing ? JSON.stringify(_area) : null,
                    area_size: _area.drawing
                      ? // ? this.mapService.calculateArea(_area.drawing[0].geometry)
                        this.toThreeDecimal(
                          this.mapService.annotationAreaSizeWithGrid$.getValue()
                        )
                      : null,
                    area_centroid: _area.drawing
                      ? this.mapService
                          .calculateCentroid(_area.drawing[0].geometry)
                          .join(', ')
                      : // ? this.mapService.annotationCentroidWithGrid$.getValue()
                        null,
                  });
                  this.mapService._setImagesRelatedToCoor(
                    _area.drawing[0].geometry
                  );
                  if (this.hasInit) {
                    this.mapScreenshot = this.mapService.getMapScreenshot();
                    this.updatedMapScreenshot = true;
                  }
                }
              });

            this.currentAnnotation = annotation;
            this.locationStore.currLocation$
              .pipe(takeUntil(this.unsubber.asObservable()))
              .subscribe((currLoc) => {
                if (currLoc.id) {
                  this.currentLocation = currLoc;
                  if (currLoc?.generic_type_template?.annotation_set) {
                    this.defectTypes =
                      currLoc.generic_type_template.annotation_set.map((i) => {
                        return this.defectTypes.find((s) => s.id === i);
                      });
                  } else {
                    this.defectTypes = this.defectTypes;
                  }
                  this.getLocationComponentList();
                  this.getSeverityList();
                  const ortho = currLoc?.map_layer_ortho;
                  const dsm = currLoc?.map_layer_dsm;
                  const threeD = currLoc?.map_layer_3d;
                  const thermal = currLoc?.map_layer_thermal;
                  const threeDMesh = currLoc?.map_layer_3d_mesh;
                  const facade = currLoc?.map_layer_facade;

                  if (ortho) {
                    this.mapLayerList.push(ortho);
                    this.mapService.isOrtho$
                      .pipe(takeUntil(this.unsubber.asObservable()))
                      .subscribe((res) => {
                        this.isOrtho = res;
                        this.reselectMapLayer();
                      });
                  }
                  if (dsm) {
                    this.mapLayerList.push(dsm);
                    this.mapService.isDSM$
                      .pipe(takeUntil(this.unsubber.asObservable()))
                      .subscribe((res) => {
                        this.isDSM = res;
                        this.reselectMapLayer();
                      });
                  }
                  if (thermal) {
                    this.mapLayerList.push(thermal);
                    this.mapService.isThermal$
                      .pipe(takeUntil(this.unsubber.asObservable()))
                      .subscribe((res) => {
                        this.isThermal = res;
                        this.reselectMapLayer();
                      });
                  }
                  if (threeDMesh) {
                    this.mapLayerList.push(threeDMesh);
                    this.mapService.isThreeDMesh$
                      .pipe(takeUntil(this.unsubber.asObservable()))
                      .subscribe((res) => {
                        this.isThreeDMesh = res;
                        this.reselectMapLayer();
                      });
                  }
                  if (threeD) {
                    this.mapLayerList.push(threeD);
                    this.mapService.isThreeD$
                      .pipe(takeUntil(this.unsubber.asObservable()))
                      .subscribe((res) => {
                        this.isThreeD = res;
                        this.reselectMapLayer();
                      });
                  }
                  if (facade) {
                    this.mapLayerList.push(facade);
                  }

                  this.form.patchValue({
                    name: annotation.name,
                    description: annotation.description,
                    location: annotation.location,
                    severity_type: annotation.severity_type.id,
                    location_component: annotation.location_component.id,
                    annotation_type: annotation.annotation_type.id,
                    map_layer: annotation.map_layer?.id || 'null',
                    area: annotation.area,
                    area_size: this.toThreeDecimal(annotation.area_size),
                    area_thumbnail: annotation.area_thumbnail,
                    area_centroid: annotation.area_centroid,
                    review_status: annotation.review_status,
                    review_remarks: annotation.review_remarks,
                  });

                  this.mapScreenshot =
                    annotation.annotation_file?.url_medium ||
                    annotation.annotation_file?.url ||
                    annotation.area_thumbnail;
                }
              });

            // this.dataService.sticky$.next(['toolkit']);

            switch (this.currentAnnotation.map_layer?.layer_type) {
              case 'facade':
                this.openlayerService.currentAnnot$.subscribe((annot) => {
                  if (annot.openlayerScreenshot) {
                    this.mapScreenshot = annot.openlayerScreenshot;
                    this.form.patchValue({
                      area_thumbnail: annot.openlayerScreenshot,
                    });
                  }
                  if (annot.openlayerDrawings)
                    this.form.patchValue({ area: annot.openlayerDrawings[0] });
                  if (annot.maplayerid)
                    this.form.patchValue({ map_layer: annot.maplayerid });
                });
                this.layerStore.toggleMapLayer(
                  this.currentAnnotation.map_layer.layer_type,
                  this.currentAnnotation.location,
                  this.currentAnnotation.map_layer,
                  true
                );
                setTimeout(() => {
                  let parsedArea = JSON.parse(annotation.area);
                  let features = parsedArea.features.map((feature) => {
                    let newFeature = {
                      ...feature,
                      properties: { severity_type: annotation.severity_type },
                    };
                    return newFeature;
                  });
                  this.openlayerService.savedAnnot$.next({
                    openlayerDrawings: [
                      JSON.stringify({ ...parsedArea, features }),
                    ],
                    zoomFitToFeature: [annotation.area],
                    enableEdit: new Date().toISOString(),
                  });
                }, 500);
                break;
              case '3d':
                this.layerStore.toggleMapLayer(
                  this.currentAnnotation.map_layer.layer_type,
                  this.currentAnnotation.location,
                  this.currentAnnotation.map_layer,
                  true
                );
                this.potreeCesiumViewerService.loadMeasurements(
                  JSON.parse(this.currentAnnotation.area)
                );
                setTimeout(() => {
                  const pointsAreaOnly = JSON.parse(
                    this.currentAnnotation.area
                  ).filter((points) => points.type == 'Area');
                  //this.mapService.getMapScreenshot();
                  let totalSize = 0;
                  pointsAreaOnly.forEach((area) => {
                    const areaSize = this.mapService.getArea(area.points);
                    totalSize += areaSize;
                  });
                  this.mapService.annotationAreaSizeWithGrid$.next(totalSize);
                }, 2000);
                break;
              default:
                this.mapService.isMapLoaded$
                  .pipe(takeUntil(this.unsubber.asObservable()))
                  .subscribe((res) => {
                    if (res) {
                      this.addAnnotation();
                      this.changeSeverity(
                        this.currentAnnotation.severity_type.id
                      );
                    } else {
                      this.mapService.map.on('draw:load', () => {
                        this.addAnnotation();
                        this.changeSeverity(
                          this.currentAnnotation.severity_type.id
                        );
                      });
                    }
                  });
                break;
            }

            this.mapService.annotationAreaSizeWithGrid$
              .pipe(takeUntil(this.unsubber.asObservable()))
              .subscribe((size) => {
                this.isSquareMeter =
                  this.mapService.allDrawings[0]?.geometry.type === 'LineString'
                    ? false
                    : true;
                this.form.patchValue({
                  area: JSON.stringify(
                    this.mapService.activeAnnotation$.getValue()
                  ),
                  area_size: this.toThreeDecimal(size),
                });
                if (
                  size > 0 &&
                  this.mapService.isShiftKeyDown$.getValue() != true &&
                  this.mapService.isThreeD$.getValue() != true &&
                  this.layerStore.isFacade$.getValue() == false
                )
                  this.setMapScreenshot();
              });

            let prevIsShiftKeyDown = false;

            this.mapService.isShiftKeyDown$
              .pipe(takeUntil(this.unsubber.asObservable()))
              .subscribe((isDown) => {
                if (isDown == null) {
                  return;
                }
                if (
                  isDown == false &&
                  this.mapService.isThreeD$.getValue() == false &&
                  this.layerStore.isFacade$.getValue() == false
                ) {
                  this.setMapScreenshot();
                }
              });

            // WIP : get centroid based on multiple drawings
            // this.mapService.annotationCentroidWithGrid$
            // .pipe(takeUntil(this.unsubber.asObservable()))
            // .subscribe(centroid => {

            //   this.form.patchValue({
            //     area_centroid: centroid
            //   });
            // });

            //get chat
            this.chatService
              .findAll([
                { key: 'filtered[0][key]', val: 'annotation' },
                { key: 'filtered[0][val]', val: this.currentAnnotation.id },
              ])
              .subscribe((chat) => {
                if (chat[0]) {
                  this.chat = chat[0];
                }
              });
            //get task
            this.taskService
              .findAll([
                { key: 'filtered[0][key]', val: 'annotation' },
                { key: 'filtered[0][val]', val: this.currentAnnotation.id },
              ])
              .subscribe((task) => {
                if (task[0]) {
                  this.task = task[0];
                }
              });

            this.form.patchValue({
              area_size: this.toThreeDecimal(annotation.area_size),
            });
          });
      });

    this.router.events
      .pipe(takeUntil(this.unsubber.asObservable()))
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.exitRoute = event.url;
        }
      });

    //reroute to summary when splitscreen is active
    this.splitViewService.onEvent$
      .pipe(takeUntil(this.unsubber.asObservable()))
      .subscribe((res) => {
        if (res.isSplitscreenOn) {
          this.router.navigate([
            'projects',
            this.currentLocation.id,
            'details',
            'summary',
          ]);
        }
      });
  }

  toThreeDecimal(area_size: number | string) {
    return Number(area_size).toFixed(3);
  }

  toggleActionBtn() {
    this.actionBtn = !this.actionBtn;
  }

  sendChat() {
    const message = this.chatForm.get('message').value.trim();
    const chat = {
      content: message,
      sender: {
        id: this.currentUser.user_id,
        username: this.currentUser.username,
      },
    };
    if (message.length > 1) {
      this.chats.push(chat);
      this.chatForm.controls['message'].reset();
    }
  }

  toggleFormIsShow(formName) {
    if (this.isFormShow.includes(formName)) {
      const index = this.isFormShow.indexOf(formName);
      if (index > -1) {
        if (formName === 'task') {
          this.taskForm = {} as any;
        }
        this.isFormShow.splice(index, 1);
      }
    } else {
      this.isFormShow.push(formName);
    }
  }

  initForms(): void {
    this.form = this.formBuilder.group({
      name: ['', Validators.required],
      description: ['', Validators.required],
      location: ['', Validators.required],
      severity_type: ['', Validators.required],
      location_component: ['', Validators.required],
      annotation_type: ['', Validators.required],
      map_layer: [''],
      area: [{ value: '', disabled: true }, Validators.required],
      area_size: [{ value: '', disabled: true }, Validators.required],
      area_thumbnail: '',
      area_centroid: '',
      kp: '',
      flight_date: '',
      status: '',
      review_status: 'pending',
      review_remarks: '',
    });

    this.chatForm = this.formBuilder.group({
      sender_id: '',
      message: '',
      defect_id: '',
    });

    this.taskForm = {} as any;

    this.galleryForm = this.formBuilder.group({
      file: '',
      defect_id: '',
    });
  }

  updateAnnotationValuesThenSave() {
    this.mapService.updateActiveAnnotation();
    setTimeout(() => {
      this.mapService.lastActiveDrawId = '';
      this.onSubmit();
    }, 1000);
  }

  onSubmit(): void {
    this.form.patchValue({
      location: this.locationStore.currentLocation.id,
    });

    if (this.form.get('review_status').value === 'reject') {
      this.form.get('review_remarks').setValidators(Validators.required);
    }

    if (this.taskForm.value) {
      if (!this.taskForm.valid) {
        this.spinner.hide();
        this.toastr.warning(
          this._t.instant('Task form not valid'),
          this._t.instant('Error')
        );
      }
    }

    if (this.form.invalid) {
      this.spinner.hide();

      const errors = [];
      Object.keys(this.form.controls).map((control) => {
        const errs = this.form.get(control).errors;

        if (!errs) return;

        let controlText = '';
        switch (control) {
          case 'name': {
            controlText = this._t.instant('Name');
            break;
          }
          case 'description': {
            controlText = this._t.instant('Description');
            break;
          }
          case 'location': {
            controlText = this._t.instant('Location');
            break;
          }
          case 'location_component': {
            controlText = this._t.instant('Location Component');
            break;
          }
          case 'annotation_type': {
            controlText = this._t.instant('Annotation Type');
            break;
          }
          case 'area': {
            controlText = this._t.instant('Area');
            break;
          }
          case 'area_size': {
            controlText = this._t.instant('Area size');
            break;
          }
        }

        Object.keys(errs).map((err) => {
          let text;
          switch (err) {
            case 'required': {
              text = this._t.instant('{{field}} is required', {
                field: controlText,
              });
              break;
            }
          }

          errors.push(text);
        });
      });
      this.toastr.error(errors.join('</br>'), this._t.instant('Error'), {
        enableHtml: true,
        timeOut: 5000,
      });
    } else {
      this._updateAnnotate();
    }
  }

  _updateAnnotate(callback?) {
    const form = this.form.getRawValue();
    if (this.splitViewService.isSplitscreenOn) {
      form.map_layer = this.splitViewService.selectedOrtho.id;
    }
    if (form.map_layer.length < 2) delete form.map_layer;

    this.spinner.show();
    // return; // for debug
    this.annotationService
      .update(this.currentAnnotation.id, form)
      .pipe(takeUntil(this.unsubber.asObservable()))
      .subscribe({
        next: async (res) => {
          if (res) {
            const area = JSON.parse(form.area);
            area.id = res.id;
            this.openlayerService.updateOneAllSavedAnnot = JSON.stringify(area);
            if (this.chats.length > 0) {
              this.chatService
                .createThread({ annotation: res.id, content: '' })
                .subscribe((thread) => {
                  this.chats.forEach((i, k) => {
                    this.chatService
                      .create({
                        chat_thread: thread.id,
                        content: i.content,
                      })
                      .subscribe();
                  });
                });
            }

            if (this.taskForm.value) {
              this.taskService
                .create({ ...this.taskForm.value, annotation: res.id })
                .subscribe(
                  (task) => {},
                  ({ error }) => {
                    const msg = error.message
                      .reduce(
                        (results, u) => ((results += u + ', '), results),
                        ''
                      )
                      .slice(0, -2);
                    this.toastr.error(msg, this._t.instant('Error'));
                  }
                );
            }

            if (
              this.mapScreenshot &&
              this.mapScreenshot.includes('data:image')
            ) {
              const file = dataURLtoFile(
                this.mapScreenshot,
                `area_thumbnail_${
                  this.locationStore.currentLocation.id
                }_${Date.now()}`
              );

              //upload area_thumnbnail to s3
              const formData = new FormData();
              formData.append('annotation', file);
              formData.append(
                'location',
                this.locationStore.currentLocation.id
              );
              formData.append('annotation_id', res.id);

              this.fileService
                .create(formData)
                .pipe(takeUntil(this.unsubber.asObservable()))
                .subscribe(async (createdFile) => {
                  await this.annotationService
                    .update(res.id, {
                      area_thumbnail: createdFile[0].url,
                      annotation_file: createdFile[0].id,
                    })
                    .toPromise();
                  if (this.currentAnnotation.annotation_file?.id) {
                    await this.fileService
                      .remove(this.currentAnnotation.annotation_file.id)
                      .toPromise();
                  }
                  await this.locUIService.refreshDefectCounts(
                    this.locationStore.currentLocation.id
                  );
                  this.toastr.success(
                    this._t.instant('Successfully annotate'),
                    this._t.instant('Success')
                  );
                  this.spinner.hide();
                  this.navigationService.back();
                });
            } else {
              await this.locUIService.refreshDefectCounts(
                this.locationStore.currentLocation.id
              );
              this.toastr.success(
                this._t.instant('Successfully annotate'),
                this._t.instant('Success')
              );
              this.spinner.hide();
              this.navigationService.back();
            }
          }
          if (callback) {
            callback(res);
          }
        },
        error: ({ error }) => {
          this.spinner.hide();
          const msg = error.message
            .reduce((results, u) => ((results += u + ', '), results), '')
            .slice(0, -2);
          this.toastr.error(msg, this._t.instant('Error'));
        },
      });
  }

  addAnnotation(): void {
    const selectedAnnot = this.currentAnnotation;
    const data = JSON.parse(selectedAnnot.area);

    this.mapService.allDrawings = data.drawing;
    if (data.grids) {
      this.mapService.allGridsData = data.grids;
    } else {
      this.mapService.allGridsData = [];
    }

    let lastSelectedDraw;
    data.drawing.forEach((drawing) => {
      this.mapService.addDraw(drawing);
      if (drawing.id == this.mapService.lastActiveDrawId) {
        lastSelectedDraw = drawing;
      }
    });

    this.mapService.hasDrawing$.next(true);

    this.mapService.addGridToDraw(
      lastSelectedDraw ? lastSelectedDraw : data.drawing[0],
      this.mapService.map,
      true
    );
    //this.mapService.addDraw(data.drawing[0]);
    // this.mapService.activeAnnotation$.next(data);
    // this.mapService.changeDrawMode('simple_select', {
    //   featureIds: [data.id],
    // });

    //this.mapService.listAnnotation.push(data);
    //this.mapService.allDrawings.push(data);
    // this.mapService.addGrid({ selected: data.selected });
    setTimeout(() => {
      this.mapService.changeDrawMode('simple_select', {
        //featureIds: data.drawing.map((i) => i.id),
        featureIds: lastSelectedDraw
          ? [this.mapService.lastActiveDrawId]
          : [data.drawing[0].id],
      });
      if (!this.mapService.isGrid$.getValue()) {
        //this.mapService.addGrid({}, data.grids.find(i => i.drawId === data.drawing[0].id))
        //this.mapService.isGrid$.next(true)
        // this.mapService.toggleGrid(true);
      }
    }, 1000);

    this.hasInit = true;
  }

  onFileSelected(e) {
    let fileName = e.target.value.split('\\').pop();
    e.target.nextElementSibling.classList.add('selected');
    e.target.nextElementSibling.innerHTML = fileName;
  }

  getSeverityList(): void {
    if (this.currentLocation?.generic_type_template?.severity_set) {
      this.severityList =
        this.currentLocation.generic_type_template.severity_set.map((i) => {
          return this.genericTypeStore.severityList.find((s) => s.id === i);
        });
    } else {
      this.severityList = this.genericTypeStore.severityList;
    }
    // this.genericTypeService
    //   .findAll([
    //     { key: 'filtered[0][key]', val: 'category' },
    //     { key: 'filtered[0][val]', val: 'severity' },
    //   ])
    //   .subscribe((response) => {
    //     this.severityList = response.data;
    //   });
  }

  getLocationComponentList(): void {
    this.locationComponentService
      .findAll([
        { key: 'filtered[0][key]', val: 'location' },
        { key: 'filtered[0][val]', val: this.currentLocation.id },
      ])
      .subscribe((response) => {
        this.locationComponentList = response.data;
      });
  }

  ngOnDestroy() {
    this.openlayerService.savedAnnot$.next({
      openlayerDrawings: undefined,
      openlayerScreenshot: null,
      enableEdit: undefined,
    });
    this.splitViewService.isEditingAnnotation = false;
    this.mapService.removeAllGrid();
    this.mapService.removeAllLineStringMarkers();
    this.mapService.isDraw$.next(null);
    this.mapService.hasDrawing$.next(false);
    this.mapService.threeDScreenshotBlob$.next(null);
    switch (this.currentAnnotation?.map_layer?.layer_type) {
      case 'ortho':
        const allDraw = this.mapService.draw.getAll();
        if (allDraw.features.length > 0) {
          const drawIds = allDraw.features.reduce(
            (acc, item) => [...acc, item.id],
            []
          );
          this.mapService.draw.delete(drawIds);
        }

        const selectedAnnot = this.currentAnnotation;
        const data = JSON.parse(selectedAnnot.area);
        data.drawing.forEach((i) => {
          this.mapService.deleteSelectedAnnot(i.getSelected);
        });
        this.layerStore.toggleAllAnnotationDataLayer(true);
        break;
      case '3d':
        // this.layerStore.toggleMapLayer(
        //   this.currentAnnotation.map_layer.layer_type,
        //   this.currentAnnotation.location,
        //   this.currentAnnotation.map_layer.external_url,
        //   true
        // );
        break;
      default:
        break;
    }

    if (this.exitRoute !== '/')
      this.locUIService.refreshCurrLocation(this.currentLocation?.id);

    this.dataService.sticky$.next([]);
    this.dataService.featureImages$.next([]);

    $('.slider-grid').attr('disabled', true);
    $('.rotateInput').attr('disabled', true);
    $('.reset-grid-button').attr('disabled', true);

    this.unsubber.next();
    this.unsubber.unsubscribe();
  }

  getDefectTypeList(): void {
    this.genericTypeService
      .findAll([
        { key: 'filtered[0][key]', val: 'category' },
        { key: 'filtered[0][val]', val: 'annotation' },
      ])
      .pipe(takeUntil(this.unsubber.asObservable()))
      .subscribe((response) => {
        this.defectTypes = response.data;
      });
  }

  changeSeverity(severity_id) {
    const allDraw = this.mapService.draw.getAll();

    let severity = this.severityList.filter(
      (severity) => severity.id === severity_id
    );
    if (severity.length > 0) {
      severity = severity[0];
    }
    if (this.layerStore.isFacade$.getValue()) {
      this.openlayerService.drawingColor$.next(severity['description']);
    }

    if (allDraw.features.length > 0) {
      allDraw.features.forEach((i) => {
        this.mapService.draw.setFeatureProperty(
          i.id,
          'severityColor',
          severity_id
        );
        this.mapService.draw.add(this.mapService.draw.get(i.id));
      });
    }

    // increase delay so it wont interfere with expensive compute like turning on draw edit mode
    setTimeout(() => {
      this.setMapScreenshot();
    }, 2000);
  }

  mapScreenshotDelay;

  setMapScreenshot() {
    if (this.layerStore.isFacade$.getValue()) {
      return;
    }
    if (this.isThreeD) {
      this.setMapScreenshot3D();
      return;
    }

    if (this.mapScreenshotDelay) {
      clearTimeout(this.mapScreenshotDelay);
    }
    this.mapScreenshotDelay = setTimeout(() => {
      this.imageCompress
        .compressFile(this.mapService.getMapScreenshot(), -1, 50, 50)
        .then((result) => {
          this.mapScreenshot = result;
          this.updatedMapScreenshot = true;
        });
    }, 2000);
  }

  setMapScreenshot3D() {
    html2canvas(document.querySelector('#potree_render_area')).then(
      (canvas) => {
        const blob = canvas.toDataURL('image/png');
        this.imageCompress.compressFile(blob, -1, 50, 50).then((result) => {
          this.mapScreenshot = result;
        });
      }
    );
  }

  showRespectiveMap(mapId) {
    const map_layer = this.currentLocation.map_layer.find(
      (i) => i.id === mapId
    );
    if (!map_layer) return;
    this.layerStore.toggleMapLayer(
      map_layer.layer_type,
      this.locationStore.currentLocation.id,
      map_layer,
      true
    );
  }

  reselectMapLayer() {
    let map_layer_id = '';
    if (this.isThreeD) {
      map_layer_id = this.currentLocation.map_layer_3d?.id;
    } else if (this.isOrtho) {
      map_layer_id = this.currentLocation.map_layer_ortho?.id;
    }
    this.form.patchValue({ map_layer: map_layer_id });
  }

  onCancelClicked(clickEvt: any) {
    this.router.navigate(['../../'], { relativeTo: this.activatedRoute });
  }
}
